import React, { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import Layout from '@/components/Layout';
import AdminDashboard from '@/components/AdminDashboard';
import ClientDashboard from '@/components/ClientDashboard';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { MessageSquare, BarChart3, Bot, Zap, CheckCircle, Star, TrendingUp, Users, DollarSign, Calendar, Heart, Loader2, Shield } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { AtomicLoadingScreen } from '@/components/ui/atomic-loader';

const Index = () => {
  const { user, login, register, isLoading: authLoading } = useAuth();
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState<'login' | 'signup'>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  if (authLoading) {
    return (
      <div className="min-h-screen bg-black">
        <AtomicLoadingScreen 
          message="Carregando Portal Átomos..." 
          className="h-screen"
        />
      </div>
    );
  }

  if (user) {
    // Redirecionar para dashboard se for cliente
    if (user.role === 'cliente') {
      // Usar navigate em vez de window.location.href
      window.location.href = '/dashboard';
      return null;
    }
    
    return (
      <Layout>
        {user.role === 'admin' ? <AdminDashboard /> : <ClientDashboard />}
      </Layout>
    );
  }

  const handleAuth = async (e: React.FormEvent) => {
    console.log('🔍 [Index] handleAuth chamado', { activeTab, email, password: password ? '***' : 'vazio' });
    e.preventDefault();
    setIsLoading(true);

    try {
      if (activeTab === 'login') {
        console.log('🔍 [Index] Iniciando login com', { email, hasPassword: !!password });
        await login(email, password);
        console.log('✅ [Index] Login realizado com sucesso');
        toast({
          title: "Login realizado com sucesso!",
          description: "Bem-vindo ao Portal Átomos."
        });
      } else {
        console.log('🔍 [Index] Iniciando registro');
        await register(name, email, password);
        console.log('✅ [Index] Registro realizado com sucesso');
        toast({
          title: "Conta criada com sucesso!",
          description: "Faça login para acessar o Portal Átomos."
        });
        setActiveTab('login');
      }
    } catch (error) {
      console.error('❌ [Index] Erro na autenticação:', error);
      toast({
        title: "Erro",
        description: activeTab === 'login' ? "Email ou senha incorretos." : "Erro ao criar conta.",
        variant: "destructive"
      });
    } finally {
      console.log('🔍 [Index] Finalizando handleAuth');
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Hero Section */}
      <section className="relative py-20 px-4 overflow-hidden">
        {/* Background Grid Pattern */}
        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0" style={{
            backgroundImage: `
              linear-gradient(rgba(34, 197, 94, 0.1) 1px, transparent 1px),
              linear-gradient(90deg, rgba(34, 197, 94, 0.1) 1px, transparent 1px)
            `,
            backgroundSize: '50px 50px'
          }}></div>
        </div>
        
        <div className="container mx-auto max-w-6xl relative z-10">
          <div className="text-center space-y-8">
            <div className="mx-auto w-24 h-24 bg-gradient-to-r from-green-400 to-green-600 rounded-2xl flex items-center justify-center shadow-lg shadow-green-500/30 animate-pulse-glow">
              <MessageSquare className="w-12 h-12 text-black" />
            </div>
            
            <div className="space-y-6">
              <h1 className="text-6xl md:text-7xl font-montserrat font-bold">
                <span className="bg-gradient-to-r from-white to-green-400 bg-clip-text text-transparent">
                  Portal Átomos
                </span>
                <span className="block text-4xl md:text-5xl mt-4 text-green-400">
                  WhatsApp Analytics
                </span>
              </h1>
              <div className="w-32 h-1 bg-gradient-to-r from-green-400 to-green-600 mx-auto rounded-full"></div>
              <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
                A plataforma mais avançada para monitorar e otimizar suas comunicações no WhatsApp. 
                Acompanhe métricas em tempo real, automatize processos e potencialize seus resultados com IA.
              </p>
            </div>

            <div className="flex flex-wrap justify-center gap-4">
              <Button 
                size="lg" 
                className="bg-gradient-to-r from-green-400 to-green-600 text-black px-8 py-4 text-lg font-semibold hover:from-green-300 hover:to-green-500 transition-all duration-300 shadow-lg shadow-green-500/30"
                onClick={() => setActiveTab('signup')}
              >
                Começar Gratuitamente
              </Button>
              <Button 
                variant="outline" 
                size="lg" 
                className="px-8 py-4 text-lg border-green-400 text-green-400 hover:bg-green-400 hover:text-black transition-all duration-300"
                onClick={() => window.location.href = '/login'}
              >
                Fazer Login
              </Button>
            </div>

            <div className="flex items-center justify-center gap-8 text-sm text-gray-400">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-400" />
                <span>Teste grátis por 30 dias</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-400" />
                <span>Integração instantânea</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-400" />
                <span>Suporte especializado</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Métricas WhatsApp Section */}
      <section className="py-16 px-4 relative">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-montserrat font-bold mb-4">
              <span className="bg-gradient-to-r from-white to-green-400 bg-clip-text text-transparent">
                Métricas Completas do WhatsApp
              </span>
            </h2>
            <p className="text-gray-300 max-w-2xl mx-auto text-lg">
              Monitore todas as suas comunicações com dados precisos e insights valiosos
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            <Card className="bg-gray-900 border-green-400/20 text-center group hover:border-green-400/50 transition-all duration-300 hover:shadow-lg hover:shadow-green-400/20">
              <CardContent className="p-6">
                <TrendingUp className="w-10 h-10 text-green-400 mx-auto mb-4 group-hover:scale-110 transition-transform" />
                <h3 className="font-semibold mb-2 text-white">Mensagens Enviadas</h3>
                <p className="text-sm text-gray-400">Total de mensagens enviadas pelos seus agentes</p>
              </CardContent>
            </Card>

            <Card className="bg-gray-900 border-green-400/20 text-center group hover:border-green-400/50 transition-all duration-300 hover:shadow-lg hover:shadow-green-400/20">
              <CardContent className="p-6">
                <MessageSquare className="w-10 h-10 text-green-400 mx-auto mb-4 group-hover:scale-110 transition-transform" />
                <h3 className="font-semibold mb-2 text-white">Mensagens Recebidas</h3>
                <p className="text-sm text-gray-400">Volume de mensagens recebidas dos clientes</p>
              </CardContent>
            </Card>

            <Card className="bg-gray-900 border-green-400/20 text-center group hover:border-green-400/50 transition-all duration-300 hover:shadow-lg hover:shadow-green-400/20">
              <CardContent className="p-6">
                <Users className="w-10 h-10 text-green-400 mx-auto mb-4 group-hover:scale-110 transition-transform" />
                <h3 className="font-semibold mb-2 text-white">Boas-vindas</h3>
                <p className="text-sm text-gray-400">Mensagens automáticas de boas-vindas enviadas</p>
              </CardContent>
            </Card>

            <Card className="bg-gray-900 border-green-400/20 text-center group hover:border-green-400/50 transition-all duration-300 hover:shadow-lg hover:shadow-green-400/20">
              <CardContent className="p-6">
                <DollarSign className="w-10 h-10 text-green-400 mx-auto mb-4 group-hover:scale-110 transition-transform" />
                <h3 className="font-semibold mb-2 text-white">Cobranças</h3>
                <p className="text-sm text-gray-400">Lembretes de pagamento e confirmações</p>
              </CardContent>
            </Card>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <Card className="bg-gray-900 border-green-400/20 hover:border-green-400/50 transition-all duration-300">
              <CardContent className="p-6">
                <div className="flex items-center mb-4">
                  <Calendar className="w-6 h-6 text-green-400 mr-3" />
                  <h3 className="text-lg font-semibold text-white">Campanhas Automáticas</h3>
                </div>
                <ul className="space-y-3 text-gray-300">
                  <li className="flex items-center"><CheckCircle className="w-4 h-4 text-green-400 mr-3" />Lembretes de pagamento</li>
                  <li className="flex items-center"><CheckCircle className="w-4 h-4 text-green-400 mr-3" />Confirmações de pagamentos</li>
                  <li className="flex items-center"><CheckCircle className="w-4 h-4 text-green-400 mr-3" />Mensagens de aniversário</li>
                  <li className="flex items-center"><CheckCircle className="w-4 h-4 text-green-400 mr-3" />Follow-ups automáticos</li>
                </ul>
              </CardContent>
            </Card>

            <Card className="bg-gray-900 border-green-400/20 hover:border-green-400/50 transition-all duration-300">
              <CardContent className="p-6">
                <div className="flex items-center mb-4">
                  <BarChart3 className="w-6 h-6 text-green-400 mr-3" />
                  <h3 className="text-lg font-semibold text-white">Analytics Avançados</h3>
                </div>
                <ul className="space-y-3 text-gray-300">
                  <li className="flex items-center"><CheckCircle className="w-4 h-4 text-green-400 mr-3" />Taxa de resposta</li>
                  <li className="flex items-center"><CheckCircle className="w-4 h-4 text-green-400 mr-3" />Tempo médio de resposta</li>
                  <li className="flex items-center"><CheckCircle className="w-4 h-4 text-green-400 mr-3" />Satisfação do cliente</li>
                  <li className="flex items-center"><CheckCircle className="w-4 h-4 text-green-400 mr-3" />Conversões por campanha</li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 px-4 bg-gray-950/50">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-montserrat font-bold mb-4">
              <span className="bg-gradient-to-r from-white to-green-400 bg-clip-text text-transparent">
                Tecnologia Avançada
              </span>
            </h2>
            <p className="text-gray-300 max-w-2xl mx-auto text-lg">
              Recursos de ponta para maximizar seus resultados no WhatsApp
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Card className="bg-gray-900 border-green-400/20 text-center group hover:border-green-400/50 transition-all duration-300 hover:shadow-xl hover:shadow-green-400/20">
              <CardContent className="p-8">
                <BarChart3 className="w-12 h-12 text-green-400 mx-auto mb-4 group-hover:scale-110 transition-transform" />
                <h3 className="text-xl font-montserrat font-semibold mb-2 text-white">Analytics em Tempo Real</h3>
                <p className="text-gray-400">Dashboards interativos com métricas atualizadas instantaneamente</p>
              </CardContent>
            </Card>

            <Card className="bg-gray-900 border-green-400/20 text-center group hover:border-green-400/50 transition-all duration-300 hover:shadow-xl hover:shadow-green-400/20">
              <CardContent className="p-8">
                <Bot className="w-12 h-12 text-green-400 mx-auto mb-4 group-hover:scale-110 transition-transform" />
                <h3 className="text-xl font-montserrat font-semibold mb-2 text-white">Integração com IA</h3>
                <p className="text-gray-400">Automação inteligente conectada ao seu sistema de gestão</p>
              </CardContent>
            </Card>

            <Card className="bg-gray-900 border-green-400/20 text-center group hover:border-green-400/50 transition-all duration-300 hover:shadow-xl hover:shadow-green-400/20">
              <CardContent className="p-8">
                <Zap className="w-12 h-12 text-green-400 mx-auto mb-4 group-hover:scale-110 transition-transform" />
                <h3 className="text-xl font-montserrat font-semibold mb-2 text-white">Performance Otimizada</h3>
                <p className="text-gray-400">Interface rápida e eficiente para máxima produtividade</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Login/Signup Section */}
      <section className="py-16 px-4" id="auth">
        <div className="container mx-auto max-w-md">
          <Card className="bg-gray-900 border-green-400/30 shadow-2xl shadow-green-400/20">
            <CardHeader className="text-center">
              <div className="flex justify-center space-x-4 mb-4">
                <Button
                  variant={activeTab === 'login' ? 'default' : 'outline'}
                  onClick={() => setActiveTab('login')}
                  className={`px-8 ${
                    activeTab === 'login' 
                      ? 'bg-green-400 text-black hover:bg-green-300' 
                      : 'border-green-400 text-green-400 hover:bg-green-400 hover:text-black'
                  }`}
                >
                  Login
                </Button>
                <Button
                  variant={activeTab === 'signup' ? 'default' : 'outline'}
                  onClick={() => setActiveTab('signup')}
                  className={`px-8 ${
                    activeTab === 'signup' 
                      ? 'bg-green-400 text-black hover:bg-green-300' 
                      : 'border-green-400 text-green-400 hover:bg-green-400 hover:text-black'
                  }`}
                >
                  Criar Conta
                </Button>
              </div>
              <CardTitle className="text-2xl font-montserrat text-white">
                {activeTab === 'login' ? 'Acessar Portal Átomos' : 'Criar Nova Conta'}
              </CardTitle>
              <CardDescription className="text-gray-400">
                {activeTab === 'login' 
                  ? 'Entre para acessar suas métricas do WhatsApp' 
                  : 'Comece a monitorar suas comunicações'
                }
              </CardDescription>
            </CardHeader>

            <CardContent>
              <form onSubmit={handleAuth} className="space-y-4">
                {activeTab === 'signup' && (
                  <div className="space-y-2">
                    <Label htmlFor="name" className="text-gray-300">Nome Completo</Label>
                    <Input
                      id="name"
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Seu nome completo"
                      required
                      className="bg-gray-800 border-gray-700 text-white focus:border-green-400"
                    />
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="email" className="text-gray-300">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="seu@email.com"
                    required
                    className="bg-gray-800 border-gray-700 text-white focus:border-green-400"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password" className="text-gray-300">Senha</Label>
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                    className="bg-gray-800 border-gray-700 text-white focus:border-green-400"
                  />
                </div>

                {activeTab === 'login' && (
                  <div className="bg-gray-800 border border-gray-700 p-4 rounded-lg text-sm text-gray-300">
                    <p className="font-medium mb-2 text-green-400">Credenciais de demonstração:</p>
                    <div className="text-xs">
                      <strong>Cliente:</strong> cliente@empresa.com / 123456
                    </div>
                  </div>
                )}

                <Button 
                  type="submit" 
                  className="w-full bg-gradient-to-r from-green-400 to-green-600 text-black hover:from-green-300 hover:to-green-500 font-semibold"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      {activeTab === 'login' ? 'Entrando...' : 'Criando conta...'}
                    </>
                  ) : (
                    activeTab === 'login' ? 'Entrar' : 'Criar Conta'
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Social Proof Section */}
      <section className="py-16 px-4 bg-gray-950/50">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="text-4xl font-montserrat font-bold mb-8">
            <span className="bg-gradient-to-r from-white to-green-400 bg-clip-text text-transparent">
              Resultados que Impressionam
            </span>
          </h2>
          
          <div className="grid md:grid-cols-3 gap-8 mb-12">
            <div className="space-y-2">
              <div className="text-5xl font-bold text-green-400">+250%</div>
              <div className="text-gray-300">Aumento no engajamento</div>
            </div>
            <div className="space-y-2">
              <div className="text-5xl font-bold text-green-400">98%</div>
              <div className="text-gray-300">Taxa de entrega</div>
            </div>
            <div className="space-y-2">
              <div className="text-5xl font-bold text-green-400">-60%</div>
              <div className="text-gray-300">Redução no tempo de resposta</div>
            </div>
          </div>

          <div className="flex justify-center items-center space-x-1 mb-4">
            {[...Array(5)].map((_, i) => (
              <Star key={i} className="w-6 h-6 fill-green-400 text-green-400" />
            ))}
            <span className="ml-2 text-gray-300">4.9/5 de satisfação</span>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Index;
